import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { useDeleteSermonQuestion, useSermonQuestions, useUpdateSermonQuestionStatus } from '../hooks/useSermonQuestions';
import { useSetLiveSermonQuestionsEnabled } from '../hooks/useToggleLiveSermon';
import { supabase } from '../services/supabase';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { Permission, hasAnyPermission } from '../utils/roles';

const Page = styled.div`
  background: var(--background);
  min-height: 100%;
  padding: 1.5rem;
`;

const Header = styled.h1`
  margin: 0 0 1rem;
  color: var(--onSurface);
  font-size: 1.7rem;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const LastUpdated = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
`;

const LiveSermonInfo = styled.p`
  margin: 0 0 1rem;
  color: #334155;
  font-size: 0.9rem;
`;

const Section = styled.section`
  margin-bottom: 1.25rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.7rem;
  color: var(--onSurface);
  font-size: 1.05rem;
`;

const Empty = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
`;

const List = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const Card = styled.article<{ $answered?: boolean }>`
  border: 1px solid ${({ $answered }) => ($answered ? '#86efac' : 'var(--outline)')};
  border-radius: 12px;
  background: ${({ $answered }) => ($answered ? '#dcfce7' : 'var(--surface)')};
  padding: 0.85rem;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
`;

const Meta = styled.p`
  margin: 0 0 0.35rem;
  color: var(--onSurfaceVariant);
  font-size: 0.8rem;
`;

const Text = styled.p`
  margin: 0 0 0.6rem;
  color: var(--onSurface);
  white-space: pre-wrap;
`;

const AnsweredButton = styled.button`
  border: 1px solid var(--primary);
  background: var(--primary);
  color: var(--surface);
  border-radius: 8px;
  padding: 0.45rem 0.7rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const DeleteButton = styled.button`
  border: 1px solid #fca5a5;
  background: #fee2e2;
  color: var(--error);
  border-radius: 999px;
  width: 1.45rem;
  height: 1.45rem;
  line-height: 1;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
`;

const GhostButton = styled.button`
  border: 1px solid var(--onSurfaceVariant);
  background: var(--surface);
  color: #334155;
  border-radius: 8px;
  padding: 0.35rem 0.6rem;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 1000;
`;

const ModalCard = styled.div`
  width: min(100%, 28rem);
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 1rem;
`;

const ModalTitle = styled.h3`
  margin: 0 0 0.75rem;
  color: var(--onSurface);
  font-size: 1.05rem;
`;

const ResponseTextarea = styled.textarea`
  width: 100%;
  min-height: 6rem;
  border: 1px solid var(--outline);
  border-radius: 8px;
  padding: 0.6rem;
  font: inherit;
  color: var(--onSurface);
  background: var(--background);
  resize: vertical;
  box-sizing: border-box;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const AdminResponseText = styled.p`
  margin: 0 0 0.6rem;
  color: #166534;
  white-space: pre-wrap;
  font-size: 0.9rem;
`;

const QuestionsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const canModerateQuestions = hasAnyPermission(roles, [Permission.MANAGE_SERMON_QUESTIONS]);
  const setLiveQuestionsEnabled = useSetLiveSermonQuestionsEnabled();
  const liveSermonQuery = useQuery({
    queryKey: ['sermons', 'live'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sermons')
        .select('id, title, questions_enabled')
        .eq('is_live', true)
        .order('live_started_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as { id: string; title: string; questions_enabled?: boolean } | null;
    },
    refetchInterval: 3000,
    refetchIntervalInBackground: true,
  });

  useEffect(() => {
    const channel = supabase
      .channel(`live-sermon-questions-page-${Date.now()}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'sermons' },
        () => {
          queryClient.invalidateQueries({ queryKey: ['sermons', 'live'] });
          queryClient.invalidateQueries({ queryKey: ['sermon-questions'] });
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const liveSermon = liveSermonQuery.data;
  const queuedQuery = useSermonQuestions(
    liveSermon?.id ? { sermonId: liveSermon.id, statuses: ['queued'] } : {}
  );
  const answeredQuery = useSermonQuestions(
    liveSermon?.id ? { sermonId: liveSermon.id, statuses: ['answered'] } : {}
  );
  const updateStatus = useUpdateSermonQuestionStatus();
  const deleteQuestion = useDeleteSermonQuestion();
  const [expandedAnsweredQuestionIds, setExpandedAnsweredQuestionIds] = useState<Record<string, boolean>>({});
  const [answeringQuestionId, setAnsweringQuestionId] = useState<string | null>(null);
  const [answerResponseDraft, setAnswerResponseDraft] = useState('');
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const intervalId = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, []);

  const lastUpdatedAt = Math.max(queuedQuery.dataUpdatedAt ?? 0, answeredQuery.dataUpdatedAt ?? 0);

  const lastUpdatedLabel = useMemo(() => {
    if (!lastUpdatedAt) return 'Last updated —';
    const diffSeconds = Math.max(0, Math.floor((nowMs - lastUpdatedAt) / 1000));
    if (diffSeconds < 5) return 'Last updated just now';
    if (diffSeconds < 60) return `Last updated ${diffSeconds}s ago`;
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) return `Last updated ${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    return `Last updated ${diffHours}h ago`;
  }, [lastUpdatedAt, nowMs]);

  const handleOpenAnswerModal = (questionId: string) => {
    setAnsweringQuestionId(questionId);
    setAnswerResponseDraft('');
  };

  const handleCloseAnswerModal = () => {
    setAnsweringQuestionId(null);
    setAnswerResponseDraft('');
  };

  const handleMarkAnswered = async () => {
    if (!answeringQuestionId) return;

    try {
      await updateStatus.mutateAsync({
        questionId: answeringQuestionId,
        nextStatus: 'answered',
        actorId: user?.id,
        adminResponse: answerResponseDraft,
      });
      handleCloseAnswerModal();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to update question status.';
      alert(message);
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    const confirmDelete = window.confirm('Delete this question?');
    if (!confirmDelete) return;

    try {
      await deleteQuestion.mutateAsync({ questionId });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to delete question.';
      alert(message);
    }
  };

  const handleToggleQuestions = async () => {
    if (!liveSermon) return;

    try {
      await setLiveQuestionsEnabled.mutateAsync({
        sermonId: liveSermon.id,
        questionsEnabled: !liveSermon.questions_enabled,
      });
    } catch (error) {
      const message = error instanceof Error
        ? error.message
        : `Unable to ${liveSermon.questions_enabled ? 'disable' : 'enable'} questions.`;
      alert(message);
    }
  };

  return (
    <Page>
      <HeaderRow>
        <Header>Questions</Header>
        <HeaderActions>
          {canModerateQuestions && liveSermon ? (
            <GhostButton
              type="button"
              onClick={() => void handleToggleQuestions()}
              disabled={setLiveQuestionsEnabled.isPending}
            >
              {setLiveQuestionsEnabled.isPending
                ? (liveSermon.questions_enabled ? 'Disabling…' : 'Enabling…')
                : (liveSermon.questions_enabled ? 'Turn Questions Off' : 'Turn Questions On')}
            </GhostButton>
          ) : null}
          <LastUpdated>{lastUpdatedLabel}</LastUpdated>
        </HeaderActions>
      </HeaderRow>

      {!liveSermon ? (
        <LiveSermonInfo>No live sermon right now. Questions list will appear when a sermon goes live.</LiveSermonInfo>
      ) : (
        <LiveSermonInfo>
          Showing questions for live sermon: <strong>{liveSermon.title}</strong>
          {!liveSermon.questions_enabled ? ' (questions currently disabled)' : ''}
        </LiveSermonInfo>
      )}

      <Section>
        <SectionTitle>Queued</SectionTitle>
        {queuedQuery.isLoading ? (
          <Empty>Loading queued questions…</Empty>
        ) : !liveSermon ? (
          <Empty>No live sermon selected.</Empty>
        ) : (queuedQuery.data?.length ?? 0) === 0 ? (
          <Empty>No queued questions right now.</Empty>
        ) : (
          <List>
            {queuedQuery.data?.map((question) => (
              <Card key={question.id}>
                <CardHeader>
                  <Meta>
                    {question.sermon_title || 'Unknown Sermon'} · {question.submitter_name || 'Anonymous'} ·{' '}
                    {new Date(question.created_at).toLocaleString()}
                  </Meta>
                  {canModerateQuestions ? (
                    <DeleteButton
                      type="button"
                      onClick={() => void handleDeleteQuestion(question.id)}
                      disabled={deleteQuestion.isPending}
                      aria-label="Delete question"
                      title="Delete question"
                    >
                      ×
                    </DeleteButton>
                  ) : null}
                </CardHeader>
                <Text>{question.question_text}</Text>
                {canModerateQuestions ? (
                  <AnsweredButton
                    type="button"
                    onClick={() => handleOpenAnswerModal(question.id)}
                    disabled={updateStatus.isPending}
                  >
                    Answered
                  </AnsweredButton>
                ) : null}
              </Card>
            ))}
          </List>
        )}
      </Section>

      <Section>
        <SectionTitle>Answered</SectionTitle>
        {answeredQuery.isLoading ? (
          <Empty>Loading answered questions…</Empty>
        ) : !liveSermon ? (
          <Empty>No live sermon selected.</Empty>
        ) : (answeredQuery.data?.length ?? 0) === 0 ? (
          <Empty>No answered questions yet.</Empty>
        ) : (
          <List>
            {answeredQuery.data?.map((question) => (
              <Card key={question.id} $answered>
                <CardHeader>
                  <Meta>
                    {question.sermon_title || 'Unknown Sermon'} · {question.submitter_name || 'Anonymous'} ·{' '}
                    {new Date(question.created_at).toLocaleString()}
                  </Meta>
                  {canModerateQuestions ? (
                    <DeleteButton
                      type="button"
                      onClick={() => void handleDeleteQuestion(question.id)}
                      disabled={deleteQuestion.isPending}
                      aria-label="Delete question"
                      title="Delete question"
                    >
                      ×
                    </DeleteButton>
                  ) : null}
                </CardHeader>
                {expandedAnsweredQuestionIds[question.id] && (
                  <>
                    <Text>{question.question_text}</Text>
                    {question.admin_response ? (
                      <AdminResponseText>
                        <strong>Response:</strong> {question.admin_response}
                      </AdminResponseText>
                    ) : null}
                  </>
                )}
                <GhostButton
                  type="button"
                  onClick={() =>
                    setExpandedAnsweredQuestionIds((current) => ({
                      ...current,
                      [question.id]: !current[question.id],
                    }))
                  }
                >
                  {expandedAnsweredQuestionIds[question.id] ? 'Collapse' : 'Expand'}
                </GhostButton>
              </Card>
            ))}
          </List>
        )}
      </Section>

      {answeringQuestionId ? (
        <ModalBackdrop onClick={handleCloseAnswerModal}>
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalTitle>Mark as answered</ModalTitle>
            <ResponseTextarea
              value={answerResponseDraft}
              onChange={(event) => setAnswerResponseDraft(event.target.value)}
              placeholder="Response to submitter (optional)"
            />
            <ModalActions>
              <GhostButton type="button" onClick={handleCloseAnswerModal}>
                Cancel
              </GhostButton>
              <AnsweredButton
                type="button"
                onClick={() => void handleMarkAnswered()}
                disabled={updateStatus.isPending}
              >
                {updateStatus.isPending ? 'Saving…' : 'Mark Answered'}
              </AnsweredButton>
            </ModalActions>
          </ModalCard>
        </ModalBackdrop>
      ) : null}
    </Page>
  );
};

export default QuestionsPage;
